import { FilterOutlined, LoadingOutlined } from "@ant-design/icons";
import { Button, Collapse, Drawer, Space, Tag, Typography } from "antd";
import { useMediaQuery } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { FILTER_AUTO_SUBMIT_ON_CHANGE } from "./filterSubmitMode";

export type ResponsiveFilterChip = {
  key: string;
  label: React.ReactNode;
  value?: React.ReactNode;
};

export interface ResponsiveFilterPanelProps {
  title?: React.ReactNode;
  activeFilters?: ResponsiveFilterChip[];
  isLoading?: boolean;
  children: React.ReactNode;
  desktopCollapseKey?: string;
  desktopDefaultOpen?: boolean;
  mobileDrawerTitle?: React.ReactNode;
  mobileTriggerLabel?: React.ReactNode;
  mobileBreakpointQuery?: string;
  mobileDrawerWidth?: number | string;
  mobileApplyLabel?: React.ReactNode;
  mobileClearLabel?: React.ReactNode;
  onApply?: () => void;
  onClear?: () => void;
  closeDrawerOnApply?: boolean;
  closeDrawerOnClear?: boolean;
}

const renderChipValue = (chip: ResponsiveFilterChip) => {
  if (chip.value === undefined || chip.value === null || chip.value === "") {
    return chip.label;
  }

  return (
    <>
      {chip.label}: {chip.value}
    </>
  );
};

const FilterActionButtons = ({
  onApply,
  onClear,
  applyLabel,
  clearLabel,
  isLoading,
  onAfterApply,
  onAfterClear,
}: {
  onApply?: () => void;
  onClear?: () => void;
  applyLabel: React.ReactNode;
  clearLabel: React.ReactNode;
  isLoading?: boolean;
  onAfterApply?: () => void;
  onAfterClear?: () => void;
}) => {
  if (!onApply && !onClear) return null;

  return (
    <Space
      style={{
        display: "flex",
        justifyContent: "flex-end",
        width: "100%",
      }}
    >
      {onClear && (
        <Button
          disabled={isLoading}
          onClick={() => {
            onClear();
            onAfterClear?.();
          }}
        >
          {clearLabel}
        </Button>
      )}
      {onApply && (
        <Button
          type="primary"
          loading={isLoading}
          onClick={() => {
            onApply();
            onAfterApply?.();
          }}
        >
          {applyLabel}
        </Button>
      )}
    </Space>
  );
};

export const ResponsiveFilterPanel = ({
  title,
  activeFilters = [],
  isLoading = false,
  children,
  desktopCollapseKey = "responsive-filter-panel",
  desktopDefaultOpen = false,
  mobileDrawerTitle,
  mobileTriggerLabel,
  mobileBreakpointQuery = "(max-width: 768px)",
  mobileDrawerWidth = 420,
  mobileApplyLabel,
  mobileClearLabel,
  onApply,
  onClear,
  closeDrawerOnApply = true,
  closeDrawerOnClear = false,
}: ResponsiveFilterPanelProps) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(mobileBreakpointQuery);
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const resolvedApplyLabel = mobileApplyLabel ?? t("Submit");
  const resolvedClearLabel = mobileClearLabel ?? t("Reset_Form");
  // Desktop Apply/Clear only in explicit mode. Mobile always keeps Apply/Clear
  // (legacy never auto-submitted on mobile).
  const showDesktopActions = !FILTER_AUTO_SUBMIT_ON_CHANGE;

  const chipList =
    activeFilters.length > 0 ? (
      <Space size={[8, 8]} wrap>
        {activeFilters.map((chip) => (
          <Tag color="green" key={chip.key}>
            {renderChipValue(chip)}
          </Tag>
        ))}
      </Space>
    ) : null;

  const headerContent = (
    <Space size={[8, 8]} wrap>
      {title && <Typography.Text>{title}</Typography.Text>}
      {chipList}
      {isLoading && <LoadingOutlined spin />}
    </Space>
  );

  if (!isMobile) {
    return (
      <Collapse
        size="small"
        defaultActiveKey={desktopDefaultOpen ? [desktopCollapseKey] : []}
        items={[
          {
            key: desktopCollapseKey,
            label: headerContent,
            children: (
              <Space direction="vertical" size={12} style={{ display: "flex", width: "100%" }}>
                {children}
                {showDesktopActions && (
                  <FilterActionButtons
                    onApply={onApply}
                    onClear={onClear}
                    applyLabel={resolvedApplyLabel}
                    clearLabel={resolvedClearLabel}
                    isLoading={isLoading}
                  />
                )}
              </Space>
            ),
          },
        ]}
      />
    );
  }

  const triggerCount = activeFilters.length;
  const resolvedTriggerLabel =
    mobileTriggerLabel ?? title ?? t("Filters");
  const resolvedDrawerTitle =
    mobileDrawerTitle ?? title ?? t("Filters");

  return (
    <>
      <Space
        direction="vertical"
        size={12}
        style={{ display: "flex", width: "100%" }}
      >
        <Space size={[8, 8]} wrap>
          <Button
            type="default"
            icon={isLoading ? <LoadingOutlined spin /> : <FilterOutlined />}
            onClick={() => setDrawerOpen(true)}
          >
            {resolvedTriggerLabel}
            {triggerCount > 0 ? ` (${triggerCount})` : ""}
          </Button>
        </Space>
        {chipList}
      </Space>

      <Drawer
        title={resolvedDrawerTitle}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={mobileDrawerWidth}
        footer={
          onApply || onClear ? (
            <FilterActionButtons
              onApply={onApply}
              onClear={onClear}
              applyLabel={resolvedApplyLabel}
              clearLabel={resolvedClearLabel}
              isLoading={isLoading}
              onAfterApply={
                closeDrawerOnApply ? () => setDrawerOpen(false) : undefined
              }
              onAfterClear={
                closeDrawerOnClear ? () => setDrawerOpen(false) : undefined
              }
            />
          ) : null
        }
      >
        {children}
      </Drawer>
    </>
  );
};
